import { useState, type ReactNode } from 'react'
import {
  Banner,
  Button,
  Chip,
  Field,
  Icon,
  Input,
  Modal,
  MoneyInput,
  PresetAmounts,
  Segmented,
  useToast,
} from '@w7/ui'
import { Amount } from '@/app/bits'
import { useTransferCredit } from '@/app/queries'
import { round2 } from '@/lib/book'
import { money } from '@/lib/format'
import { ROLE_LABEL, type Account } from '@/data/types'
import { capFor, zeroingMove } from './settle'
import './downline.css'

/**
 * Moving credit, as a popup.
 *
 * This replaces `TransferDrawer`, a full-height side sheet carrying two
 * four-item definition lists (eight figures), two stacked balance previews, a
 * preset row and a two-row textarea. Dropping the definition lists is what makes
 * this compact: the figures that mattered are folded into the cap hint and the
 * before/after strip, where they are read IN CONTEXT rather than scanned in a
 * grid and mentally re-joined.
 *
 * Three things survive, because they are the three things that stop a mistake:
 *   1. the cap, and WHY it is the cap — never a bare "maximum exceeded"
 *   2. before and after on BOTH sides, in one grid so the two "after" figures
 *      line up vertically
 *   3. error text that names the actual constraint and the figure behind it
 *
 * Exported so `MemberPage`'s `CreditDrawer` can adopt it in a later change.
 */

/** Round down to a tidy figure, so a preset never lands a rupee over a cap. */
function roundDown(value: number): number {
  if (!(value > 0)) return 0
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)))
  return Math.floor(value / magnitude) * magnitude
}

function presetsFor(creditLimit: number, cap: number): number[] {
  const candidates = [0.1, 0.25, 0.5, 1].map((fraction) => roundDown(creditLimit * fraction))
  return [...new Set([...candidates, roundDown(cap)])]
    .filter((amount) => amount > 0 && amount <= cap)
    .sort((a, b) => a - b)
}

/**
 * Bold one figure inside a sentence that is owned elsewhere.
 *
 * The cap sentence lives in `capFor` because the plan, the run and the report
 * quote the same wording; re-typing it here with a `<strong>` in the middle is
 * exactly how the dialog and the plan start explaining the same cap two ways.
 */
function emphasise(text: string, needle: string): ReactNode {
  const at = text.indexOf(needle)
  if (at === -1) return text
  return (
    <>
      {text.slice(0, at)}
      <strong>{needle}</strong>
      {text.slice(at + needle.length)}
    </>
  )
}

export function TransferDialog({
  self,
  account,
  initialDirection,
  initialAmount,
  onClose,
}: {
  self: Account
  account: Account
  initialDirection: 'give' | 'take'
  /** Click-to-zero prefill. Undefined when opened from the Give/Take buttons. */
  initialAmount?: number
  onClose: () => void
}) {
  const [direction, setDirection] = useState<'give' | 'take'>(initialDirection)
  const [raw, setRaw] = useState(initialAmount ? String(initialAmount) : '')
  // Tracks whether what is in the box is OURS or the operator's. Flipping the
  // toggle throws away a prefill; it must never throw away a typed figure.
  const [prefilled, setPrefilled] = useState(initialAmount !== undefined)
  const [remark, setRemark] = useState('')
  const [submitted, setSubmitted] = useState(false)
  /* One key per opening of this dialog, reused by every attempt made from it: a
     retry after a failure must not be able to move the money twice. */
  const [idempotencyKey] = useState(() => crypto.randomUUID())

  const transfer = useTransferCredit()
  const toast = useToast()

  const giving = direction === 'give'
  const cleaned = raw.replace(/[^\d.]/g, '')
  const parsed = cleaned === '' ? 0 : Number(cleaned)
  const amount = Number.isFinite(parsed) ? round2(parsed) : 0

  const capInfo = capFor(self, account, direction)
  const { cap, freeToGive, headroom } = capInfo
  const zeroing = zeroingMove(account)

  // The four strings below are carried over verbatim from the drawer this
  // replaces — they were already good, and rewording an error the operator has
  // learnt to recognise is a change with no upside.
  const problem = !(amount > 0)
    ? 'Enter an amount greater than zero.'
    : giving && amount > freeToGive
      ? `You have ${money(freeToGive)} free to give — your ${money(self.balance)} balance less the ${money(self.exposure)} already committed to open bets.`
      : giving && amount > headroom
        ? `${account.username} can hold ${money(headroom)} more before reaching their ${money(account.creditLimit)} credit limit. Raise the limit first, or give less.`
        : !giving && amount > account.balance
          ? account.balance > 0
            ? `${account.username} holds ${money(account.balance)}. You cannot take more than they hold.`
            : `${account.username} holds ${money(account.balance)}, so there is nothing to take. Settle with them instead.`
          : null

  // "Enter an amount" is not an error until they try to submit; a breached cap
  // is worth saying the MOMENT it is typed — including on the very first render
  // when the click-to-zero prefill already exceeds it.
  const showProblem = problem !== null && (submitted || amount > 0)
  const overCap = amount > 0 && amount > cap && cap > 0

  const selfAfter = round2(self.balance + (giving ? -amount : amount))
  const theirAfter = round2(account.balance + (giving ? amount : -amount))
  // A take can legitimately strip credit that is already committed to open
  // bets. It has never been blocked here and is not blocked now — the operator
  // sweeping an account on a Monday means it — but it is said loudly.
  // ...but only where the movement could actually happen. At a cap of zero the
  // submit is disabled, so forecasting what the move "leaves them with" is
  // describing a future that this dialog has already refused to create — and it
  // reads as a second, contradictory answer beside the banner that says nothing
  // can move at all.
  const stripsExposure = !giving && amount > 0 && theirAfter < account.exposure && cap > 0

  const setAmount = (next: number, fromPreset: boolean) => {
    setRaw(String(next))
    setPrefilled(fromPreset)
    if (transfer.isError) transfer.reset()
  }

  const switchDirection = (next: 'give' | 'take') => {
    setDirection(next)
    setSubmitted(false)
    transfer.reset()
    if (prefilled) {
      setRaw('')
      setPrefilled(false)
    }
  }

  const submit = () => {
    setSubmitted(true)
    if (problem) return
    transfer.mutate(
      { accountId: account.id, amount, direction, remark: remark.trim(), idempotencyKey },
      {
        onSuccess: (result) => {
          // Every figure below is quoted from the ledger's reply, never from
          // what was predicted here; the tables refresh off the invalidation.
          const settled = round2(result.settlementAfter) === 0
          toast.success(
            `${money(amount)} ${giving ? 'given to' : 'taken from'} ${account.username}`,
            `You now hold ${money(result.from.id === self.id ? result.from.balance : result.to.balance)} · ${account.username} now holds ${money(result.from.id === account.id ? result.from.balance : result.to.balance)} · ${settled ? 'settled to zero' : `${money(Math.abs(result.settlementAfter))} still outstanding`} · ref ${result.transfer.ref}`,
          )
          onClose()
        },
      },
    )
  }

  const outstanding =
    account.settlement === 0
      ? ''
      : account.settlement > 0
        ? ` · ${money(account.settlement)} outstanding in your favour`
        : ` · ${money(-account.settlement)} outstanding to them`

  return (
    <Modal
      open
      onClose={onClose}
      title={giving ? `Give credit to ${account.username}` : `Take credit from ${account.username}`}
      subtitle={`${ROLE_LABEL[account.role]} · ${account.displayName}${outstanding}`}
      footer={
        <>
          <span className="w7-spacer" />
          <Button variant="secondary" onClick={onClose} disabled={transfer.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={giving ? 'give' : 'take'}
            busy={transfer.isPending}
            disabled={problem !== null}
            onClick={submit}
          >
            {amount > 0
              ? `${giving ? 'Give' : 'Take'} ${money(amount)}`
              : giving
                ? 'Give credit'
                : 'Take credit'}
          </Button>
        </>
      }
    >
      <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
        <Segmented
          label="Direction of the movement"
          value={direction}
          onChange={switchDirection}
          items={[
            { id: 'give', label: 'Give to them' },
            { id: 'take', label: 'Take from them' },
          ]}
        />

        <Field
          label="Amount"
          required
          aside={
            cap > 0 ? (
              // FLOORED, never rounded. A chip reading "₹6,786" against a ledger
              // that accepts ₹6,785.56 is a papercut you only discover at submit.
              <Chip size="sm" tone="muted">
                Max {money(Math.floor(cap), { round: true })}
              </Chip>
            ) : null
          }
          hint={emphasise(capInfo.because, money(cap))}
          error={showProblem ? problem : null}
        >
          {({ id, describedBy }) => (
            <MoneyInput
              id={id}
              aria-describedby={describedBy}
              invalid={showProblem}
              value={raw}
              autoComplete="off"
              onChange={(event) => {
                setRaw(event.target.value)
                setPrefilled(false)
                if (transfer.isError) transfer.reset()
              }}
            />
          )}
        </Field>

        {/* The one-tap way out of an over-cap prefill. Shrinking the prefill
            silently would hide the single most important fact on the row: that
            some of it is going to stay outstanding. */}
        {overCap ? (
          <div className="w7-row-wrap" style={{ gap: 6 }}>
            <button
              type="button"
              className="w7-filterchip"
              aria-label={`Use the maximum you can move now, ${money(cap)} — ${money(round2(amount - cap))} would stay outstanding.`}
              onClick={() => setAmount(cap, true)}
            >
              Settle what you can — {money(cap)}
            </button>
          </div>
        ) : null}

        {/* Cap of exactly nothing. `overCap` above cannot cover this — it is
            gated on `cap > 0`, because "settle what you can" has nothing to
            offer when what you can is zero. Without this branch the dialog sits
            in its worst state: an amount prefilled, an error explaining it
            cannot move, a settle-to-zero preset that only re-enters the same
            error, and no sentence telling the operator what happens instead.
            The wording is the bulk report's, deliberately — `settle.ts` already
            argues that one cap explained two ways is a support ticket, and this
            is the same cap on the same row, reached through the other door. */}
        {zeroing && zeroing.direction === direction && cap <= 0 ? (
          <Banner tone="warn" title="This cannot be settled right now">
            {capInfo.because} {money(zeroing.amount)} stays outstanding
            {giving ? '' : ` against ${account.username}`}. Settle when they have funded, or move
            the other way.
          </Banner>
        ) : null}

        <div className="w7-row-wrap" style={{ gap: 6 }}>
          {/* Settle-to-zero comes FIRST and reads as the recommended choice. It
              disappears when the toggle is flipped away from its direction — a
              "settle to zero" that moves the settlement further FROM zero is
              meaningless — and when the cap is zero, where it would be an action
              that cannot succeed. Over-cap-but-possible still shows it: there
              the operator has "settle what you can" to fall back on. */}
          {zeroing && zeroing.direction === direction && cap > 0 ? (
            <button
              type="button"
              className="w7-filterchip w7-settle-preset"
              aria-label={`Settle to zero, ${money(zeroing.amount)}`}
              title={
                zeroing.amount > cap
                  ? `More than you can move right now — picking it will show what the cap is and what would stay outstanding.`
                  : undefined
              }
              onClick={() => setAmount(zeroing.amount, true)}
            >
              Settle to zero {money(zeroing.amount, { round: true })}
            </button>
          ) : null}
          <PresetAmounts
            presets={presetsFor(account.creditLimit, cap)}
            format={(preset) => money(preset, { round: true })}
            onPick={(preset) => setAmount(preset, true)}
          />
        </div>

        {/* Both sides of the movement, before and after, in ONE grid so the two
            "after" figures land in the same column. A preview, not a ledger
            projection — after submit the toast quotes the ledger's own reply. */}
        <div className="w7-move" aria-label="What this movement would leave on each side">
          <span className="w7-micro">You ({self.username})</span>
          <span className="w7-move-before">
            <Amount value={self.balance} />
          </span>
          <span className="w7-move-after">
            <Icon name="arrow-right" size={14} className="w7-move-arrow" />{' '}
            <Amount value={selfAfter} />
          </span>

          <span className="w7-micro">{account.username}</span>
          <span className="w7-move-before">
            <Amount value={account.balance} />
          </span>
          <span className="w7-move-after">
            <Icon name="arrow-right" size={14} className="w7-move-arrow" />{' '}
            <Amount value={theirAfter} />
          </span>
        </div>

        {stripsExposure ? (
          <Banner tone="warn">
            This leaves {account.username} with {money(theirAfter)} against{' '}
            {money(account.exposure)} of open bets. Their next bet will be rejected.
          </Banner>
        ) : null}

        <Field label="Remark" hint="Optional. It shows on both statements and in the transfer report.">
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              maxLength={80}
              value={remark}
              placeholder="What this movement is for"
              onChange={(event) => setRemark(event.target.value)}
            />
          )}
        </Field>

        {transfer.isError ? (
          <Banner tone="error" title="The credit did not move">
            {(transfer.error as Error)?.message} Your amount is still entered — fix the problem and
            try again, or close this and nothing has happened.
          </Banner>
        ) : null}
      </div>
    </Modal>
  )
}
